import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { isLessThanBreakpoint } from 'dpl/util/grid';
import useQuiz from 'dpl/shared/hooks/useQuiz';
import FormContext from 'dpl/shared/form/utils/context';
import FullPageQuizHeader from 'dpl/components/FullPageQuiz/FullPageQuizHeader';
import FullPageQuizProgressBar from 'dpl/components/FullPageQuiz/FullPageQuizProgressBar';
import FullPageQuizMobileNavigation from 'dpl/components/FullPageQuiz/FullPageQuizMobileNavigation';
import FullPageQuizMobileNavigationV2 from 'dpl/components/FullPageQuiz/FullPageQuizMobileNavigationV2';
import { isMobileUA, isNativeAppUA } from 'dpl/shared/utils';

import { defaultGetSectionComponent, SECTION_TYPES } from '../utils';

const IS_MOBILE_UA = isMobileUA();
const IS_NATIVE_UA = isNativeAppUA();

const quizLayoutDefaultPropOnNextClick = () => {};
const quizLayoutDefaultPropOnSubmitClick = () => {};
const quizLayoutDefaultPropOnSectionSubmit = () => {};
const quizLayoutDefaultPropOnQuizSubmit = () => {};
const quizLayoutDefaultPropOnNewSectionRendered = () => {};
const quizLayoutDefaultPropOnAnsweredEvent = () => {};
/** @param {any} props */
export default function QuizLayout({
  quizCreatedAt = null,
  submissionId,
  getSectionComponent = defaultGetSectionComponent,
  getQuestionComponent = undefined,
  onNextClick = quizLayoutDefaultPropOnNextClick,
  onSubmitClick = quizLayoutDefaultPropOnSubmitClick,
  onExitClick,
  onSectionSubmit = quizLayoutDefaultPropOnSectionSubmit,
  onQuizSubmit = quizLayoutDefaultPropOnQuizSubmit,
  onNewSectionRendered = quizLayoutDefaultPropOnNewSectionRendered,
  onAnsweredEvent = quizLayoutDefaultPropOnAnsweredEvent,
  hideProgressBar = false,
  headerCloseButtonLabel = 'Save & Exit',
  v2MobileNav = false,
  showNavForMiddleInterstices = false
}) {
  const [isTransitioning, _setIsTransitioning] = useState(false);
  const [topNavMargin, setTopNavMargin] = useState(0);
  const [bottomNavMargin, setBottomNavMargin] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [isResizing, setIsResizing] = useState(false);

  const topNav = useRef();
  const bottomNav = useRef();
  const containerRef = useRef();

  const {
    isValid,
    isSectionSubmitting,
    isQuizSubmitting,
    isDirty,
    loaded,
    quiz,
    currentSection,
    currentSectionIdx,
    submitSection,
    submitQuiz,
    startQuiz,
    incrementSectionIdx,
    decrementSectionIdx,
    resetFormState,
    formContext
  } = useQuiz(submissionId, onAnsweredEvent);

  function setIsTransitioning(value) {
    // FIXME: prevents transitions in test env to stop interactions with the
    // question before the transition is complete
    if (!CONFIG.isTest) {
      _setIsTransitioning(value);
    }
  }

  useLayoutEffect(() => {
    if (!isTransitioning && loaded) {
      // update section's form data only once transition is complete and when
      // new section's data is available.
      resetFormState();

      onNewSectionRendered(quiz, currentSection);

      const {
        force_footer: forceFooter,
        force_header: forceHeader,
        hide_footer: hideFooter
      } = currentSection.component_props || {};

      // hide top nav when first section is rendered
      // TODO: replace this assumption with component_props for all quizzes
      const topMargin =
        currentSection.first && !forceHeader ? -topNav.current.clientHeight : 0;

      const showCTAForInterstice =
        showNavForMiddleInterstices &&
        !currentSection.first &&
        !currentSection.last;

      // hide bottom nav when any interstice is rendered, or when a section
      // opts out via hide_footer (e.g. multi-apply screens that render their
      // own self-contained footer and don't want the shared one)
      // TODO: replace this assumption with component_props for all quizzes
      const bottomMargin =
        hideFooter ||
        (currentSection.type === SECTION_TYPES.INTERSTICE &&
          !forceFooter &&
          !showCTAForInterstice)
          ? -bottomNav.current.clientHeight
          : 0;

      setTopNavMargin(topMargin);
      setBottomNavMargin(bottomMargin);
    }
  }, [loaded, isTransitioning, currentSectionIdx]);

  // Alternate background for header on success screen on desktop
  const showAlternateBackgroundHeader =
    currentSection.last && !IS_NATIVE_UA && !IS_MOBILE_UA;

  // Track container width changes using ResizeObserver
  useEffect(() => {
    if (!containerRef.current) return null;

    let rafId = null;
    const resizeObserver = new window.ResizeObserver(entries => {
      if (entries[0]) {
        setIsResizing(true);
        setContainerWidth(entries[0].contentRect.width);

        // Disable the transform animation while we're adjusting to the new
        // measured width (otherwise translate3d changes will slide the quiz).
        if (rafId) window.cancelAnimationFrame(rafId);
        rafId = window.requestAnimationFrame(() => setIsResizing(false));
      }
    });

    resizeObserver.observe(containerRef.current);
    // Set initial width
    setContainerWidth(containerRef.current.clientWidth);

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
    };
  }, []);

  /**
   * Don't allow any keyboard input (particularly tabs which cause layout
   * shift) while sections are transitioning.
   */
  useEffect(() => {
    function handleKeyDown(e) {
      e.preventDefault();
    }

    if (isTransitioning) {
      document.activeElement.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.activeElement?.removeEventListener('keydown', handleKeyDown);
    };
  }, [isTransitioning]);

  async function submitSectionIfNeeded() {
    if ((isDirty || !currentSection.answered) && isValid) {
      const ret = await submitSection();

      if (!(ret instanceof Error)) {
        onSectionSubmit();
      }

      return ret;
    }

    return false;
  }

  function handleTransitionEnd(e) {
    if (e.target === e.currentTarget) {
      setIsTransitioning(false);
    }
  }

  async function handleSaveAndExitClick() {
    const ret = await submitSectionIfNeeded();

    if (!(ret instanceof Error)) {
      onExitClick();
    }
  }

  async function handleNextClick(_event, answers) {
    onNextClick(quiz, currentSection, answers);

    const ret = await submitSectionIfNeeded();

    if (!(ret instanceof Error)) {
      setIsTransitioning(true);
      incrementSectionIdx();
    }
  }

  function handleBackClick() {
    setIsTransitioning(true);
    decrementSectionIdx();
  }

  async function handleQuizSubmit() {
    onSubmitClick(quiz, currentSection);
    const ret = await submitSectionIfNeeded();

    if (!(ret instanceof Error)) {
      await submitQuiz();
      onQuizSubmit();
    }
  }

  const canAdvance =
    isValid && !isSectionSubmitting && !isQuizSubmitting && !isTransitioning;

  const isLessThanMd = isLessThanBreakpoint('md');

  return (
    <div
      className="QuizLayout DSFullPageQuiz h-100 overflow-hidden"
      ref={containerRef}
    >
      {!loaded ? (
        <div className="h-100 loading-spinner" />
      ) : (
        <div className="flex flex-column h-100">
          <div
            ref={topNav}
            className="QuizLayout__topNav"
            style={{ marginTop: `${topNavMargin}px` }}
            data-test-id="top-navbar"
          >
            <FullPageQuizHeader
              closeButtonLabel={headerCloseButtonLabel}
              onSaveClick={handleSaveAndExitClick}
              onBackClick={
                currentSection.first ||
                currentSection.component_props?.hide_back_button
                  ? null
                  : handleBackClick
              }
              showAlternateBackgroundHeader={showAlternateBackgroundHeader}
            />
          </div>
          <div className="flex-auto h-100">
            <div
              className="flex flex-nowrap h-100"
              onTransitionEnd={handleTransitionEnd}
              data-test-id="sections-container"
              style={{
                width: `${quiz.sections.length * containerWidth}px`,
                // don't want to transition when new sections are added and
                // transform changes, or when container is resizing
                transition: isResizing ? 'none' : 'transform 300ms ease',
                transform: `translate3d(${
                  -currentSectionIdx * containerWidth
                }px, 0px, 0px)`
              }}
            >
              <FormContext.Provider value={formContext}>
                {quiz.sections.map((section, idx) => (
                  <div
                    key={section.uuid}
                    style={{ flex: `0 0 ${containerWidth}px` }}
                    className="h-100"
                    data-test-id={`section-${section.uuid}`}
                  >
                    {(() => {
                      const isCurrentSection = currentSectionIdx === idx;
                      // don't render sections outside of view, so 1. they
                      // don't waste memory and let us render leaner
                      // payloads; and 2. they can't be focused using tab.
                      // However, *do* render adjacent sections while they
                      // transition out of view to prevent their jarring
                      // disappearance.
                      if (
                        !isCurrentSection &&
                        (!isTransitioning ||
                          Math.abs(currentSectionIdx - idx) > 1)
                      ) {
                        return null;
                      }

                      const SectionComponent = getSectionComponent(section);

                      return (
                        <div className="overflow-y-auto no-scrollbars overflow-x-hidden h-100">
                          <SectionComponent
                            quizCreatedAt={quizCreatedAt}
                            quizSubmissionId={submissionId}
                            title={section.title}
                            subtitle={section.subtitle}
                            questions={section.questions}
                            canAdvance={canAdvance}
                            isCurrentSection={isCurrentSection}
                            isLastSection={section.last}
                            onNextClick={handleNextClick}
                            onStartClick={startQuiz}
                            onQuizSubmit={handleQuizSubmit}
                            getQuestionComponent={getQuestionComponent}
                            {...section.component_props}
                          />
                        </div>
                      );
                    })()}
                  </div>
                ))}
              </FormContext.Provider>
            </div>
          </div>
          <footer
            ref={bottomNav}
            style={{ marginBottom: `${bottomNavMargin}px` }}
            data-test-id="bottom-navbar"
            className={classnames('QuizLayout__bottomNav w-100 f0 z-1', {
              'box-shadow-2': isLessThanMd
            })}
          >
            {!hideProgressBar && (
              <FullPageQuizProgressBar
                currentStep={currentSectionIdx}
                steps={quiz.sections}
                progressBarColor="stone-700"
              />
            )}
            {isLessThanMd && !v2MobileNav && (
              <FullPageQuizMobileNavigation
                onPreviousClick={handleBackClick}
                onNextClick={handleNextClick}
                onSubmit={handleQuizSubmit}
                isLastStep={currentSection.last}
                isPreviousDisabled={currentSection.first}
                isNextDisabled={!canAdvance}
                isSubmitDisabled={!canAdvance}
                // FIXME: ScreeningQuiz auto submits when answering the
                // CallWhenSection so the submit button should be hidden
                shouldShowSubmit={
                  currentSection?.component_name !== 'CallWhenSection'
                }
              />
            )}
            {isLessThanMd && v2MobileNav && (
              <FullPageQuizMobileNavigationV2
                onNextClick={handleNextClick}
                onSubmit={handleQuizSubmit}
                isNextDisabled={!canAdvance}
                isSubmitDisabled={!canAdvance}
              />
            )}
          </footer>
        </div>
      )}
    </div>
  );
}

QuizLayout.propTypes = {
  quizCreatedAt: PropTypes.string,
  submissionId: PropTypes.number.isRequired,
  getSectionComponent: PropTypes.func,
  getQuestionComponent: PropTypes.func,
  onNextClick: PropTypes.func,
  onSubmitClick: PropTypes.func,
  onExitClick: PropTypes.func.isRequired,
  onSectionSubmit: PropTypes.func,
  onQuizSubmit: PropTypes.func,
  onNewSectionRendered: PropTypes.func,
  onAnsweredEvent: PropTypes.func,
  hideProgressBar: PropTypes.bool,
  headerCloseButtonLabel: PropTypes.string,
  v2MobileNav: PropTypes.bool,
  // Will not show the bottom nav for first/last screens, but will show for all interstices in between
  showNavForMiddleInterstices: PropTypes.bool
};
